import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ButtonLink } from '../../../../../components';
import { t } from '../../../../../i18n';
import UrlTo from '../../../../../url-to';
import { failureRedCircleIcon, successGreenCircleIcon } from '../../../../../components/assets';
import { loadCoursesCheckoutInformation } from '../../redux/actions';
import { loadGiftCardCheckoutInformation } from '../../../gift-card-checkout/redux/actions';
import { useStripeContext } from '../../components/stripe-provider';
import { logTrace, flushLogs } from '../../../../../utilities/log';
import PaymentSuccessInstructions from '../../components/payment-success-instructions';
import CheckoutContext from '../../checkout-context';
import './styles.scss';

const Status = {
  SUCCESS: 'success',
  FAILURE: 'failure',
  PROCESSING: 'processing',
};

const StripePaymentResultScreen = ({ match }) => {
  const purchasedCourseId = match.params.id;
  const stripe = useStripeContext();
  const [paymentResult, setPaymentResult] = useState({
    status: '',
    title: '',
    text: '',
  });
  const { isCoursePayment } = useContext(CheckoutContext);
  const dispatch = useDispatch();

  const { email, name, clientSecret } = useMemo(() => {
    const queryParams = new URLSearchParams(window.location.search);
    return {
      email: queryParams.get('email'),
      name: queryParams.get('name'),
      clientSecret: queryParams.get('payment_intent_client_secret'),
    };
  }, []);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    dispatch(
      isCoursePayment
        ? loadCoursesCheckoutInformation(purchasedCourseId, { email })
        : loadGiftCardCheckoutInformation(),
    );

    logTrace('Courses/Checkout', 'Screen loaded', { email, purchasedCourseId });
    flushLogs();

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case 'succeeded':
          setPaymentResult({
            status: Status.SUCCESS,
            title: 'DIGITAL_COURSES.CHECKOUT.PURCHASE_RESULT.SUCCESS.TITLE',
            text: `DIGITAL_COURSES.CHECKOUT.PURCHASE_RESULT.SUCCESS.BODY`,
          });
          break;
        case 'processing':
          setPaymentResult({
            status: Status.PROCESSING,
            title: 'DIGITAL_COURSES.CHECKOUT.PURCHASE_RESULT.PROCESSING.TITLE',
            text: `DIGITAL_COURSES.CHECKOUT.PURCHASE_RESULT.PROCESSING.BODY`,
          });
          break;
        default:
          setPaymentResult({
            status: Status.FAILURE,
            title: 'DIGITAL_COURSES.CHECKOUT.PURCHASE_RESULT.FAILURE.TITLE',
            text: `DIGITAL_COURSES.CHECKOUT.PURCHASE_RESULT.FAILURE.BODY`,
          });
      }
    });
  }, [stripe]);

  if (!paymentResult.status) {
    return (
      <div className="courses-checkout-result__placeholder placeholder-glow">
        <div className="placeholder" />
      </div>
    );
  }

  const tryAgainPath = isCoursePayment
    ? UrlTo.coursesCheckout(purchasedCourseId, { email, name })
    : UrlTo.coursesGiftCardCheckout({ email, name });
  const okPath = isCoursePayment ? UrlTo.coursesSpecificCourse(purchasedCourseId) : UrlTo.coursesHome();

  return (
    <div className="courses-checkout-result">
      <img
        src={paymentResult.status === Status.FAILURE ? failureRedCircleIcon : successGreenCircleIcon}
        alt="Payment status"
      />
      <h1>{t(paymentResult.title)}</h1>
      <pre>{t(paymentResult.text)}</pre>
      {isCoursePayment && paymentResult.status === Status.SUCCESS && <PaymentSuccessInstructions />}
      <ButtonLink
        className="courses-checkout-result__purchase-result-button"
        type="green"
        text={t(paymentResult.status === Status.FAILURE ? 'TRY_AGAIN' : 'OK')}
        href={paymentResult.status === Status.FAILURE ? tryAgainPath : okPath}
      />
    </div>
  );
};

export default StripePaymentResultScreen;
